import { useRef, useState } from 'react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AdminHub, FirmwareRelease } from '../../../domain/Hub';

// Mientras un hub se actualiza el estado cambia en segundos: se refresca seguido.
const REFRESH_MS = 5000;

// Versiones publicadas desde el taller y a qué hubs mandarlas.
export function useAdminFirmware() {
  const { admin } = useRepositories();
  const [releases, setReleases] = useState<FirmwareRelease[]>([]);
  const [hubs, setHubs] = useState<AdminHub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // La versión que se está por mandar y los hubs elegidos.
  const [deploying, setDeploying] = useState<FirmwareRelease | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const [{ releases }, { hubs }] = await Promise.all([admin.fetchFirmware(), admin.fetchHubs()]);
      setReleases(releases);
      setHubs(hubs);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las versiones.');
    } finally {
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
    timer.current = setInterval(load, REFRESH_MS);
  });
  useIonViewWillLeave(() => {
    if (timer.current) clearInterval(timer.current);
  });

  const openDeploy = (release: FirmwareRelease) => {
    setDeploying(release);
    setSelected([]);
  };

  const toggle = (hubId: string) =>
    setSelected((current) => (current.includes(hubId) ? current.filter((id) => id !== hubId) : [...current, hubId]));

  const deploy = async () => {
    if (!deploying || selected.length === 0) return;
    setBusy(true);
    try {
      const { results } = await admin.deployFirmware(deploying.id, selected);
      const sent = results.filter((r) => r.status === 'enviada').length;
      const skipped = results.length - sent;
      setToast(`Orden enviada a ${sent} hub${sent === 1 ? '' : 's'}${skipped ? `; ${skipped} sin conexión, no les llegó` : ''}.`);
      setDeploying(null);
      await load();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'No se pudo mandar la versión.');
    } finally {
      setBusy(false);
    }
  };

  return { releases, hubs, loading, error, toast, setToast, deploying, setDeploying, selected, toggle, openDeploy, deploy, busy };
}
